/**
 * 统一树形组件
 * 支持节点右侧菜单（三个点）
 */

import React from 'react';
import { Tree, Dropdown, Menu, Button } from 'antd';
import { MoreOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import type { TreeNode } from '../utils/treeBuilder';
import {
  AppstoreOutlined,
  DatabaseOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

interface UnifiedTreeProps {
  treeData: TreeNode[];
  selectedKeys: React.Key[];
  expandedKeys: React.Key[];
  onSelect: (keys: React.Key[], info: any) => void;
  onExpand: (keys: React.Key[]) => void;
  onAddNode: (type: string, parent?: TreeNode) => void;
  onEditNode: (node: TreeNode) => void;
  onDeleteNode: (node: TreeNode) => void;
  loading?: boolean;
}

const UnifiedTree: React.FC<UnifiedTreeProps> = ({
  treeData,
  selectedKeys,
  expandedKeys,
  onSelect,
  onExpand,
  onAddNode,
  onEditNode,
  onDeleteNode,
  loading,
}) => {
  // 获取节点图标
  const getIcon = (type: string) => {
    switch (type) {
      case 'service':
        return <AppstoreOutlined style={{ color: '#1890ff' }} />;
      case 'page':
        return <AppstoreOutlined style={{ color: '#52c41a' }} />;
      case 'component':
        return <AppstoreOutlined style={{ color: '#fa8c16' }} />;
      case 'dataset':
        return <DatabaseOutlined style={{ color: '#722ed1' }} />;
      case 'field':
        return <FileTextOutlined style={{ color: '#13c2c2' }} />;
      default:
        return null;
    }
  };

  // 获取可添加的子节点类型
  const getAddableTypes = (nodeType: string): Array<{ key: string; label: string }> => {
    switch (nodeType) {
      case 'service':
        return [{ key: 'page', label: '添加页面' }];
      case 'page':
        return [{ key: 'component', label: '添加组件' }];
      case 'component':
        return [
          { key: 'component', label: '添加子组件' },
          { key: 'dataset', label: '关联数据集' },
        ];
      case 'dataset':
        return [{ key: 'field', label: '添加字段' }];
      default:
        return [];
    }
  };

  // 将 TreeNode 转换为 Ant Design Tree 的 DataNode
  const convertToDataNode = (node: TreeNode): DataNode => {
    const addableTypes = getAddableTypes(node.type);
    const hasMenu = addableTypes.length > 0 || node.type !== 'field';

    return {
      key: node.key,
      title: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
          onClick={(e) => {
            e.stopPropagation();
            // 传递完整的节点信息
            onSelect([node.key], { 
              node: { 
                key: node.key, 
                data: node,
                title: node.title,
                type: node.type,
              } 
            });
          }}
        >
          <span>
            {getIcon(node.type)} {node.title}
          </span>
          {hasMenu && (
            <Dropdown
              menu={{
                items: [
                  ...addableTypes.map((item) => ({
                    key: item.key,
                    label: item.label,
                    icon: <PlusOutlined />,
                    onClick: () => onAddNode(item.key, node),
                  })),
                  { type: 'divider' },
                  {
                    key: 'edit',
                    label: '编辑',
                    icon: <EditOutlined />,
                    onClick: () => onEditNode(node),
                  },
                  ...(node.type !== 'service'
                    ? [
                        {
                          key: 'delete',
                          label: '删除',
                          icon: <DeleteOutlined />,
                          danger: true,
                          onClick: () => onDeleteNode(node),
                        },
                      ]
                    : []),
                ],
              }}
              trigger={['click']}
            >
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{ opacity: 0.6 }}
              />
            </Dropdown>
          )}
        </div>
      ),
      children: node.children?.map(convertToDataNode),
      isLeaf: node.isLeaf,
    };
  };

  const dataNodes = treeData.map(convertToDataNode);

  return (
    <div>
      {loading && <div style={{ padding: 16, textAlign: 'center' }}>加载中...</div>}
      <Tree
        treeData={dataNodes}
        selectedKeys={selectedKeys}
        expandedKeys={expandedKeys}
        onSelect={onSelect}
        onExpand={onExpand}
        showLine
        showIcon={false}
        blockNode
      />
    </div>
  );
};

export default UnifiedTree;
